/**
 * 客户端插件生命周期钩子
 * install/activate/deactivate
 */
export interface PluginLifecycle {
  name: string
  version: string
  install?: (context: PluginContext) => Promise<void> | void
  activate?: (context: PluginContext) => Promise<void> | void
  deactivate?: (context: PluginContext) => Promise<void> | void
  uninstall?: (context: PluginContext) => Promise<void> | void
}

export interface PluginContext {
  config: Record<string, any>
  log: (message: string) => void
}

export type PluginState = 'installed' | 'active' | 'inactive' | 'error'

export interface PluginInfo {
  name: string
  version: string
  state: PluginState
  installedAt: number
  error?: string
}

export class PluginLifecycleManager {
  private plugins = new Map<string, { lifecycle: PluginLifecycle; info: PluginInfo }>()
  private logs: string[] = []

  /** 安装插件 */
  async install(plugin: PluginLifecycle, config?: Record<string, any>): Promise<boolean> {
    const ctx = this.createContext(config)
    try {
      await plugin.install?.(ctx)
      this.plugins.set(plugin.name, {
        lifecycle: plugin,
        info: { name: plugin.name, version: plugin.version, state: 'installed', installedAt: Date.now() },
      })
      return true
    } catch (err: any) {
      this.plugins.set(plugin.name, {
        lifecycle: plugin,
        info: { name: plugin.name, version: plugin.version, state: 'error', installedAt: Date.now(), error: err.message },
      })
      return false
    }
  }

  /** 激活插件 */
  async activate(name: string): Promise<boolean> {
    const entry = this.plugins.get(name)
    if (!entry || entry.info.state === 'active') return false
    try {
      await entry.lifecycle.activate?.(this.createContext())
      entry.info.state = 'active'
      return true
    } catch (err: any) {
      entry.info.state = 'error'
      entry.info.error = err.message
      return false
    }
  }

  /** 停用插件 */
  async deactivate(name: string): Promise<boolean> {
    const entry = this.plugins.get(name)
    if (!entry || entry.info.state !== 'active') return false
    try {
      await entry.lifecycle.deactivate?.(this.createContext())
      entry.info.state = 'inactive'
      return true
    } catch {
      return false
    }
  }

  /** 卸载插件 */
  async uninstall(name: string): Promise<boolean> {
    const entry = this.plugins.get(name)
    if (!entry) return false
    await entry.lifecycle.uninstall?.(this.createContext())
    this.plugins.delete(name)
    return true
  }

  /** 获取插件信息 */
  getInfo(name: string): PluginInfo | null {
    return this.plugins.get(name)?.info ?? null
  }

  /** 列出所有插件 */
  list(): PluginInfo[] {
    return [...this.plugins.values()].map((p) => p.info)
  }

  /** 获取日志 */
  getLogs(): string[] {
    return [...this.logs]
  }

  private createContext(config?: Record<string, any>): PluginContext {
    return {
      config: config ?? {},
      log: (msg: string) => this.logs.push(msg),
    }
  }
}
